import React from "react";
import {
    Box,
    Typography,
    LinearProgress,
    IconButton,
    Tooltip,
    Chip,
    Card,
    CardContent,
    Divider
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import {MoreVert} from "@mui/icons-material";

// Styled components
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    border: "none",
    backgroundColor: "white",
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#f8f9fa",
        borderRadius: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            color: theme.palette.text.secondary,
        },
    },
    "& .MuiDataGrid-virtualScroller": {
        backgroundColor: "white",
    },
    "& .MuiDataGrid-row": {
        "&:nth-of-type(even)": {
            backgroundColor: "#fafafa",
        },
        "&:hover": {
            backgroundColor: "#f5f8ff",
        },
        cursor: "pointer",
    },
    "& .MuiDataGrid-cell": {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5),
        fontSize: "0.9rem",
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
        outline: "none",
    },
}));

// Empty state component
const EmptyState = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
    backgroundColor: "#fff",
    borderRadius: 8,
    textAlign: "center",
}));

// Linear progress with label component
const LinearProgressWithLabel = (props) => {
    const { value } = props;
    let color = "error";

    if (value >= 75) {
        color = "success";
    } else if (value >= 50) {
        color = "warning";
    } else if (value >= 25) {
        color = "info";
    }

    return (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress variant="determinate" {...props} color={color} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(
                    value
                )}%`}</Typography>
            </Box>
        </Box>
    );
};

// Property status chip
const StatusChip = ({ occupancy }) => {
    const value = parseInt(occupancy.replace("%", "").trim());
    let color = "error";
    let label = "Low";

    if (value >= 75) {
        color = "success";
        label = "High";
    } else if (value >= 50) {
        color = "warning";
        label = "Medium";
    }

    return (
        <Chip
            label={label}
            color={color}
            size="small"
            sx={{
                fontSize: "0.75rem",
                height: 22,
                fontWeight: 500
            }}
        />
    );
};

const styles = {
    statusChip: {
        fontWeight: 600,
        borderRadius: "16px",
    },
};

const PropertyGrid = ({ properties, onViewProperty, onDeleteProperty }) => {
    // Check if properties is empty
    const hasProperties = properties && properties.length > 0;

    // Define columns for the data grid
    const columns = [
        {
            field: "property_name",
            headerName: "Property Name",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ fontWeight: 500, color: "#2C5283" }}>
                    {params.row.property_name}
                </Box>
            ),
        },
        {
            field: "location",
            headerName: "Location",
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                    <Typography variant="body2">{params.row.location}</Typography>
                </Box>
            ),
        },
        {
            field: "total_property_units",
            headerName: "Units",
            minWidth: 100,
            flex: 0.5,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ApartmentIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                    <Typography variant="body2">{params.row.total_property_units}</Typography>
                </Box>
            ),
        },
        {
            field: "occupancy",
            headerName: "Occupancy",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => {
                const value = parseInt(params.row.occupancy.replace("%", "").trim());
                return (
                    <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
                        <LinearProgressWithLabel value={value} />
                        <StatusChip occupancy={params.row.occupancy} />
                    </Box>
                );
            },
        },
        {
            field: "expected_revenue",
            headerName: "Expected Rent",
            minWidth: 150,
            flex: 0.8,
            align: "right",
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", color: "success.main", fontWeight: 500 }}>
                    <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {params.row.expected_revenue.toLocaleString("en-US", {
                        style: "currency",
                        currency: "KSH",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    })}
                </Box>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 120,
            flex: 0.5,
            sortable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box sx={{ display: "flex" }}>
                   {/* <Chip
                        label="View Details"
                        color="info"
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewProperty(params.row.id);
                        }}
                        deleteIcon={<MoreVert/>}
                        variant="outlined"
                        style={{cursor: "pointer"}}
                        sx={styles.statusChip}
                    />*/}
                    <Tooltip title="View Details">
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewProperty(params.row.id);
                            }}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Property">
                        <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteProperty(params.row.id, params.row.property_name);
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Card sx={{ borderRadius: "0 0 8px 8px", overflow: "hidden" }}>
            {hasProperties ? (
                <StyledDataGrid
                    rows={properties}
                    columns={columns}
                    disableSelectionOnClick
                    pageSize={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    autoHeight
                    density="standard"
                    disableColumnMenu
                    onRowClick={(params) => onViewProperty(params.row.id)}
                    sx={{ minHeight: 500 }}
                />
            ) : (
                <EmptyState>
                    <ApartmentIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No properties found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Add your first property to start managing your real estate portfolio.
                    </Typography>
                </EmptyState>
            )}
        </Card>
    );
};

export default PropertyGrid;